/**
 * Unified Promotions Manager - Multi-Currency Edition
 * 
 * Manages both GWP (Gift With Purchase) and Tiered Discounts simultaneously across multiple currencies
 * 
 * Configuration: Hardcoded in CONFIG below
 * 
 * GWP Feature:
 * - Adds/removes gift item when cart subtotal crosses threshold (currency-specific)
 * - Gift item variant ID, threshold, and discount code can differ per currency
 * 
 * Tiered Discounts Feature:
 * - Multiple tiers with different thresholds and discount codes per currency
 * - Example: USD might have 10%/15%/20% at $35/$55/$75
 * - CAD might have 10%/15%/20% at $45/$70/$95
 * 
 * Discount Code Management:
 * - Attempts to apply discount codes as configured
 * - Falls back gracefully if multiple codes aren't supported
 * 
 * Only active in currencies that are configured
 * Debug mode: Controlled via DEBUG flag below
 *
 * ============================================================================
 * !! IMPORTANT — KEEP THE CART PROGRESS BAR IN SYNC !!
 * ============================================================================
 * This file only controls the cart-level promotion (which gift/discount is
 * applied). The customer-facing cart progress bar is configured SEPARATELY and
 * will NOT update automatically. Whenever you change the GWP/TIERS config here
 * (e.g. swap a tiered discount for a free gift, or change a threshold), you also
 * need to update:
 *   1. config/settings_data.json  -> "cart_milestones_list"
 *      Format per line: CURRENCY:dollarValue:MilestoneName[:icon]
 *      Icon controls the badge AND the messaging: "gift" => Free Gift wording,
 *      "tag" => discount wording. (e.g. USD:65:Free Gift:gift)
 *   2. snippets/cart-progress-bar.liquid -> messaging/threshold logic
 *   3. snippets/cart-progress-bar-icon.liquid -> if you need a new icon
 * Make sure the progress bar threshold + reward type match the config below,
 * otherwise the bar will advertise the wrong reward to customers.
 * ============================================================================
 */

(function() {
  'use strict';

  // ============================================================================
  // CONFIGURATION - Edit values below for your store
  // NOTE: If you change the reward/threshold below, also update the cart progress
  //       bar (config/settings_data.json "cart_milestones_list" +
  //       snippets/cart-progress-bar.liquid). See the file header for details.
  // ============================================================================
  
  const CONFIG = {
    DEBUG: false, // Set to true for detailed console logging

    // GWP Configuration by Currency
    // Format: { currency: { thresholdCents, gifts: [ { variantId, productId?, discountCode }, ... ] } }
    // Gifts are evaluated in order. If the customer has gift 1 as a paid cart item,
    // the script pivots to gift 2, then gift 3, etc.
    GWP: {
      // ENABLED: GWP free gift at $65 USD (USD tiered discount disabled below in favor of this).
      USD: {
        thresholdCents: 6500, // $65 USD
        discountCode: 'FREEGIFT',
        gifts: [
          {
            variantId: 54199784636700,
            label: 'Golden Vanilla',
            discountCode: 'FREEGIFT',
            extraProperties: { '_gwp_variant_selector': 'true' }
          },
          {
            variantId: 54199865573660,
            label: 'Love Letter',
            discountCode: 'FREEGIFT',
            extraProperties: { '_gwp_variant_selector': 'true' }
          },
          {
            variantId: 54199869014300,
            label: 'Super Pink',
            discountCode: 'FREEGIFT',
            extraProperties: { '_gwp_variant_selector': 'true' }
          },
          {
            variantId: 54199973871900,
            label: 'Petal Bloom',
            discountCode: 'FREEGIFT',
            extraProperties: { '_gwp_variant_selector': 'true' }
          }
        ]
      },
      // CAD: {
      //   thresholdCents: 6000, // $60 CAD
      //   gifts: [
      //     {
      //       variantId: 52348479340916, // Update with CAD-specific variant
      //       discountCode: 'FREEGIFT'
      //     }
      //   ]
      // }
      // Add more currencies as needed:
      // EUR: { thresholdCents: 4500, gifts: [ { variantId: 123456, discountCode: 'FREEGIFT' } ] }
    },
    
    // Tiered Discounts by Currency
    // Format: { currency: [ {code, thresholdCents, percentage, label}, ... ] }
    // Must be ordered from highest threshold to lowest for proper evaluation
    TIERS: {
      USD: [
        // DISABLED: "15% off $65" tiered discount retired in favor of the GWP free gift at $65 USD (see GWP.USD above).
        // Kept for record keeping — re-enable by uncommenting (and disable the GWP.USD block) if we swap back.
        // {
        //   code: '15off65',
        //   thresholdCents: 6500, // $65 USD
        //   percentage: 15,
        //   label: '15% off $65'
        // }
        // Previous USD tier examples kept for record keeping:
        // {
        //   code: 'BFCM_20',
        //   thresholdCents: 7500, // $75 USD
        //   percentage: 20,
        //   label: '20% off $75+'
        // },
        // {
        //   code: '10off60',
        //   thresholdCents: 6000, // $60 USD
        //   percentage: 10,
        //   label: '10% off $60+'
        // },
        // {
        //   code: 'BFCM_10',
        //   thresholdCents: 3500, // $35 USD
        //   percentage: 10,
        //   label: '10% off $35+'
        // }
      ],
      CAD: [
        // {
        //   code: 'BFCM_20',
        //   thresholdCents: 11000, // $110 CAD
        //   percentage: 20,
        //   label: '20% off $110+'
        // },
        // {
        //   code: 'BFCM_15',
        //   thresholdCents: 8000, // $80 CAD
        //   percentage: 15,
        //   label: '15% off $80+'
        // },
        // {
        //   code: 'BFCM_10',
        //   thresholdCents: 5500, // $55 CAD
        //   percentage: 10,
        //   label: '10% off $55+'
        // }
      ]
      // Add more currencies as needed:
      // EUR: [ { code: 'BFCM_20_EUR', thresholdCents: 7000, percentage: 20, label: '20% off €70+' } ]
    },
    
    // Common configuration
    GWP_PROPERTY_KEY: '_gwp_item',
    GWP_PROPERTY_VALUE: 'true'
  };

  // ============================================================================
  // STATE TRACKING
  // ============================================================================

  let isProcessing = false;
  let lastCartTotal = null;
  let lastCartSignature = null;
  let lastCurrency = null;
  let currentDiscountCodes = []; // Track all active discount codes
  let userSelectedGiftVariantId = null;
  const giftImageCache = new Map();
  const giftAvailabilityCache = new Map();
  const giftUrlCache = new Map();
  const giftTitleCache = new Map();
  let giftSwatchRenderTimer = null;
  let giftSwatchRenderGeneration = 0;
  let processCartRenderGeneration = 0;
  let giftSwapGeneration = 0;
  let giftSwapDebounceTimer = null;
  const GIFT_SWAP_DEBOUNCE_MS = 1000;
  let isReanchoringGWPDiscount = false;

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================
  
  /**
   * Debug logging utility
   */
  function log(message, data = null) {
    if (CONFIG.DEBUG) {
      console.log(`[Unified Promotions] ${message}`, data || '');
    }
  }

  /**
   * Get current cart currency
   */
  function getCurrentCurrency() {
    return window.Shopify?.currency?.active || 
           window.themeVariables?.settings?.cartCurrency || 
           'USD';
  }

  /**
   * Check if promotions are configured for current currency
   */
  function isConfiguredForCurrency(currency) {
    return CONFIG.GWP[currency] !== undefined || CONFIG.TIERS[currency] !== undefined;
  }

  /**
   * Get GWP configuration for currency
   */
  function getGWPConfig(currency) {
    return CONFIG.GWP[currency] || null;
  }

  /**
   * Get ordered GWP gift candidates for currency
   */
  function getGWPCandidates(currency) {
    const gwpConfig = getGWPConfig(currency);
    return gwpConfig?.gifts || [];
  }

  /**
   * Gifts that declare the variant-selector property — these are the dropdown options.
   */
  function getGWPGiftSelectorCandidates(currency) {
    return getGWPCandidates(currency).filter(
      gift => gift.extraProperties?._gwp_variant_selector === 'true'
    );
  }

  /**
   * Get tier configuration for currency
   */
  function getTiersConfig(currency) {
    return CONFIG.TIERS[currency] || [];
  }

  /**
   * Fetch the latest cart state from Shopify.
   */
  async function fetchFreshCart() {
    if (!window.themeVariables?.routes?.cartUrl) return null;

    try {
      const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
      if (!response.ok) return null;
      return response.json();
    } catch (error) {
      log('Failed to fetch fresh cart', error);
      return null;
    }
  }

  function countGWPItems(cart) {
    if (!cart?.items) return 0;
    return cart.items.filter(isGWPLineItem).length;
  }

  /**
   * Remove duplicate managed GWP lines, keeping at most one correct variant.
   */
  async function dedupeGWPItems(cart, desiredVariantId = null) {
    const gwpItems = cart?.items?.filter(isGWPLineItem) || [];
    if (gwpItems.length === 0) return false;

    const hasCorrectSingle = gwpItems.length === 1 &&
      (!desiredVariantId || String(gwpItems[0].id) === String(desiredVariantId));

    if (hasCorrectSingle) return false;

    log('Removing duplicate or incorrect GWP line(s)', {
      count: gwpItems.length,
      desiredVariantId
    });
    await removeGWPItem(cart);
    return true;
  }

  function getGWPDiscountCode(currency, selectedGWPConfig = null) {
    return selectedGWPConfig?.discountCode ||
      getGWPConfig(currency)?.discountCode ||
      null;
  }

  function getPaidLineWithVariant(cart, variantId) {
    if (!variantId) return null;
    return getPaidCartItems(cart).find(item => String(item.id) === String(variantId)) || null;
  }

  function lineHasActiveDiscount(lineItem) {
    if (!lineItem) return false;
    if (lineItem.final_line_price === 0 && (lineItem.original_line_price || 0) > 0) {
      return true;
    }
    if (Array.isArray(lineItem.line_level_discount_allocations) &&
        lineItem.line_level_discount_allocations.length > 0) {
      return true;
    }
    return (lineItem.original_line_price || 0) > (lineItem.final_line_price || 0);
  }

  function cartHasActiveDiscount(cart) {
    if (!cart) return false;
    if (Array.isArray(cart.cart_level_discount_applications) &&
        cart.cart_level_discount_applications.length > 0) {
      return true;
    }
    return (cart.items || []).some(lineHasActiveDiscount);
  }

  /**
   * FREEGIFT should zero out the managed GWP line, not a separately-added paid copy
   * of the same variant.
   */
  function isGWPDiscountMisallocated(cart) {
    const gwpItem = getCurrentGWPItem(cart);
    if (!gwpItem) return false;

    const paidDuplicate = getPaidLineWithVariant(cart, gwpItem.id);
    if (!paidDuplicate) return false;

    if (!cartHasActiveDiscount(cart)) return false;

    const gwpIsFree = gwpItem.final_line_price === 0;
    const paidIsDiscounted = lineHasActiveDiscount(paidDuplicate);

    return paidIsDiscounted || !gwpIsFree;
  }

  function snapshotPaidLine(lineItem) {
    const properties = { ...(lineItem.properties || {}) };
    delete properties[CONFIG.GWP_PROPERTY_KEY];
    delete properties._gwp_variant_selector;
    delete properties._ISGWP;

    return {
      variantId: lineItem.id,
      quantity: lineItem.quantity,
      properties,
      selling_plan: lineItem.selling_plan_allocation?.selling_plan?.id || null
    };
  }

  async function removePaidLine(lineKey) {
    const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        id: lineKey,
        quantity: 0
      })
    });

    if (!response.ok) {
      throw new Error(`Failed to remove paid duplicate line: ${response.status}`);
    }

    return response.json();
  }

  async function restorePaidLine(snapshot) {
    const item = {
      id: snapshot.variantId,
      quantity: snapshot.quantity
    };

    if (snapshot.properties && Object.keys(snapshot.properties).length > 0) {
      item.properties = snapshot.properties;
    }
    if (snapshot.selling_plan) {
      item.selling_plan = snapshot.selling_plan;
    }

    const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ items: [item] })
    });

    if (!response.ok) {
      throw new Error(`Failed to restore paid duplicate line: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Apply FREEGIFT while a paid duplicate of the gift variant is in the cart.
   * Shopify allocates cart-level product discounts to the first matching line, so
   * temporarily remove the paid line, discount the GWP line, then restore it.
   */
  async function applyGWPDiscountWithPaidDuplicate(cart, currency, selectedGWPConfig, paidDuplicate) {
    const discountCode = getGWPDiscountCode(currency, selectedGWPConfig);
    const gwpItem = getCurrentGWPItem(cart);

    if (!discountCode || !gwpItem || !paidDuplicate) {
      return cart;
    }

    const paidSnapshot = snapshotPaidLine(paidDuplicate);

    log('Applying FREEGIFT to GWP line only (paid duplicate present)', {
      variantId: gwpItem.id,
      paidKey: paidDuplicate.key,
      gwpKey: gwpItem.key
    });

    await removeAllDiscountCodes();
    await removePaidLine(paidDuplicate.key);
    await applySingleDiscountCode(discountCode);
    await restorePaidLine(paidSnapshot);

    const updatedCart = await fetchFreshCart();
    if (updatedCart && isGWPDiscountMisallocated(updatedCart)) {
      console.warn('[Unified Promotions] FREEGIFT still misallocated after paid-duplicate safe apply');
    }

    return updatedCart || cart;
  }

  async function ensureGWPLineDiscounted(cart, currency, selectedGWPConfig) {
    if (!cart || isReanchoringGWPDiscount) return cart;

    const gwpItem = getCurrentGWPItem(cart);
    const paidDuplicate = gwpItem ? getPaidLineWithVariant(cart, gwpItem.id) : null;

    if (!gwpItem || !paidDuplicate || !isGWPDiscountMisallocated(cart)) {
      return cart;
    }

    isReanchoringGWPDiscount = true;

    try {
      return await applyGWPDiscountWithPaidDuplicate(
        cart,
        currency,
        selectedGWPConfig,
        paidDuplicate
      );
    } finally {
      isReanchoringGWPDiscount = false;
    }
  }

  /**
   * Check if a cart line is managed by GWP logic
   */
  function isGWPLineItem(item) {
    const properties = item?.properties || {};
    return properties[CONFIG.GWP_PROPERTY_KEY] === CONFIG.GWP_PROPERTY_VALUE;
  }

  /**
   * Check if cart contains a GWP item
   */
  function hasGWPItem(cart) {
    if (!cart || !cart.items) return false;
    
    return cart.items.some(isGWPLineItem);
  }

  /**
   * Get current GWP item in cart
   */
  function getCurrentGWPItem(cart) {
    if (!cart || !cart.items) return null;

    return cart.items.find(isGWPLineItem) || null;
  }

  /**
   * Get paid cart items, excluding any managed GWP line
   */
  function getPaidCartItems(cart) {
    if (!cart || !cart.items) return [];

    return cart.items.filter(item => !isGWPLineItem(item));
  }

  /**
   * Build a compact signature for paid cart contents
   */
  function getCartSignature(cart) {
    return getPaidCartItems(cart)
      .map(item => `${item.id}:${item.product_id || ''}:${item.quantity || 0}`)
      .sort()
      .join('|');
  }

  /**
   * Set of variant IDs sitting inside in-cart bundles, read from the
   * `data-in-cart-bundle-components` attribute on the mini-cart form.
   */
  function getInCartBundleComponentVariantIds() {
    const ids = new Set();
    const el = document.querySelector('[data-in-cart-bundle-components]');
    if (!el) return ids;
    el.getAttribute('data-in-cart-bundle-components').split(',').forEach((id) => {
      const trimmed = id.trim();
      if (trimmed) ids.add(trimmed);
    });
    return ids;
  }

  /**
   * Check whether a paid cart line or in-cart bundle component matches a gift.
   */
  function cartContainsGiftProductOrVariant(cart, gift) {
    if (!gift) return false;

    const giftVariantId = String(gift.variantId);

    const matchesTopLevelLine = getPaidCartItems(cart).some(item => {
      const matchesVariant = String(item.id) === giftVariantId;
      const matchesProduct = gift.productId !== undefined &&
        String(item.product_id) === String(gift.productId);

      return matchesVariant || matchesProduct;
    });

    if (matchesTopLevelLine) return true;

    return getInCartBundleComponentVariantIds().has(giftVariantId);
  }

  /**
   * Whether a gift variant is in stock for selection / auto-add.
   * Uses config `available: false`, then cached Shopify variant data when present.
   */
  function isGiftVariantAvailable(gift) {
    if (!gift) return false;
    if (gift.available === false) return false;

    const cached = giftAvailabilityCache.get(String(gift.variantId));
    if (cached !== undefined) return cached;

    return true;
  }

  function buildGiftVariantFallbackUrl(variantId) {
    const root = window.Shopify?.routes?.root || '/';
    const normalizedRoot = root.endsWith('/') ? root : `${root}/`;
    return `${normalizedRoot}variants/${variantId}`;
  }

  function cacheGiftVariantUrl(variantId, url) {
    if (!variantId || !url) return;
    giftUrlCache.set(String(variantId), url);
  }

  async function fetchGiftVariantAvailability(gift) {
    if (!gift) return false;
    if (gift.available === false) return false;

    const variantId = String(gift.variantId);
    if (giftAvailabilityCache.has(variantId)) {
      if (!giftUrlCache.has(variantId)) {
        giftUrlCache.set(variantId, buildGiftVariantFallbackUrl(variantId));
      }
      return giftAvailabilityCache.get(variantId);
    }

    try {
      const response = await fetch(`${window.Shopify.routes.root}variants/${gift.variantId}.js`);
      if (response.ok) {
        const variant = await response.json();
        const available = variant.available !== false;
        giftAvailabilityCache.set(variantId, available);

        const imageUrl = variant.featured_image?.src ||
          variant.featured_image?.url ||
          null;
        if (imageUrl) {
          giftImageCache.set(variantId, imageUrl);
        }

        cacheGiftVariantUrl(variantId, variant.url || buildGiftVariantFallbackUrl(variantId));

        const displayTitle = (variant.name || '').split(' - ')[0].replace(/\.$/, '').trim();
        if (displayTitle) {
          giftTitleCache.set(variantId, displayTitle);
        }

        return available;
      }
    } catch (error) {
      log('Failed to fetch gift variant availability', error);
    }

    cacheGiftVariantUrl(variantId, buildGiftVariantFallbackUrl(variantId));
    return true;
  }

  function getGiftVariantUrl(gift) {
    if (!gift) return null;
    const variantId = String(gift.variantId);
    return giftUrlCache.get(variantId) || buildGiftVariantFallbackUrl(variantId);
  }

  function getGiftVariantTitle(gift) {
    if (!gift) return null;
    return giftTitleCache.get(String(gift.variantId)) || gift.label || null;
  }

  async function refreshSelectorGiftAvailability(currency) {
    const candidates = getGWPGiftSelectorCandidates(currency);
    await Promise.all(candidates.map((gift) => fetchGiftVariantAvailability(gift)));
    return candidates.filter(isGiftVariantAvailable);
  }

  /**
   * First gift in config order that is in stock and not already owned in the cart.
   * If every gift is already present (standalone or bundle component), fall back to
   * the first in-stock gift, then the first gift in config order.
   */
  function getDefaultGWPGift(cart, currency) {
    const giftCandidates = getGWPCandidates(currency);
    if (!giftCandidates.length) return null;

    const eligibleGifts = giftCandidates.filter((gift) => {
      if (!isGiftVariantAvailable(gift)) return false;
      return !cartContainsGiftProductOrVariant(cart, gift);
    });

    if (eligibleGifts.length > 0) {
      return eligibleGifts[0];
    }

    return giftCandidates.find((gift) => isGiftVariantAvailable(gift)) || giftCandidates[0];
  }

  /**
   * Variant id to show as selected in the cart color dropdown.
   */
  function getSelectorDefaultVariantId(cart, currency) {
    const selectorCandidates = getGWPGiftSelectorCandidates(currency);

    if (userSelectedGiftVariantId) {
      const chosenGift = selectorCandidates.find(
        (gift) => String(gift.variantId) === String(userSelectedGiftVariantId)
      );
      if (chosenGift && isGiftVariantAvailable(chosenGift)) {
        return String(userSelectedGiftVariantId);
      }
    }

    const currentGift = getCurrentGWPItem(cart);
    if (currentGift) {
      return String(currentGift.id);
    }

    const defaultGift = getDefaultGWPGift(cart, currency);
    return defaultGift ? String(defaultGift.variantId) : null;
  }

  /**
   * Select which gift color to give for this cart.
   *
   * Rules:
   *  - Cart must meet the threshold.
   *  - A color is only eligible if it is in stock AND not already in the cart
   *    (whether as a standalone line or as a component inside a bundle).
   *  - If the customer has explicitly chosen a color (via the cart selector) and
   *    that color is still eligible, honor it.
   *  - Otherwise default to the first eligible color (config order).
   *  - If every color is already in the cart, default back to the first in-stock
   *    gift in config order.
   */
  function getSelectedGWPConfig(cart, currency, subtotalCents) {
    const gwpConfig = getGWPConfig(currency);

    if (!gwpConfig || subtotalCents < gwpConfig.thresholdCents) {
      return null;
    }

    // Honor an explicit color choice from the cart dropdown (config selector gifts only).
    if (userSelectedGiftVariantId) {
      const chosenGift = getGWPGiftSelectorCandidates(currency).find(
        (gift) => String(gift.variantId) === String(userSelectedGiftVariantId)
      );
      if (
        chosenGift &&
        isGiftVariantAvailable(chosenGift) &&
        !cartContainsGiftProductOrVariant(cart, chosenGift)
      ) {
        return chosenGift;
      }
    }

    return getDefaultGWPGift(cart, currency);
  }
  
    /**
     * Calculate cart subtotal excluding GWP items
     */
    function getCartSubtotalExcludingGWP(cart) {
      if (!cart || !cart.items) return 0;
      
      return cart.items.reduce((total, item) => {
        if (isGWPLineItem(item)) {
          return total; // Don't count GWP items in subtotal
        }
        
        return total + (item.final_line_price || 0);
      }, 0);
    }
  
  /**
   * Determine which discount tier applies based on cart total and currency
   * Returns the tier object or null if no tier applies
   */
  function getApplicableTier(subtotalCents, currency) {
    const tiers = getTiersConfig(currency);
    
    // Tiers are ordered from highest to lowest, so first match is the best one
    for (const tier of tiers) {
      if (subtotalCents >= tier.thresholdCents) {
        return tier;
      }
    }
    return null;
  }

  /**
   * Determine which discount codes should be active based on cart total and currency
   * Returns array of discount code strings
   */
  function getApplicableDiscountCodes(subtotalCents, currency, selectedGWPConfig = null, cart = null) {
    const applicableCodes = [];
    
    // Check for tiered discount
    const tier = getApplicableTier(subtotalCents, currency);
    if (tier) {
      applicableCodes.push(tier.code);
    }
    
    // GWP discount only applies when the managed GWP line is in the cart.
    const gwpConfig = selectedGWPConfig || (() => {
      const configuredGWP = getGWPConfig(currency);
      if (!configuredGWP || subtotalCents < configuredGWP.thresholdCents) return null;

      return getGWPCandidates(currency)[0] || null;
    })();

    const gwpDiscountCode = getGWPDiscountCode(currency, gwpConfig);
    const gwpLinePresent = cart ? hasGWPItem(cart) : !!gwpConfig;

    if (gwpDiscountCode && gwpLinePresent) {
      applicableCodes.push(gwpDiscountCode);
    }
    
    log('Applicable discount codes determined', {
      currency,
      subtotalCents,
      codes: applicableCodes
    });
    
    return applicableCodes;
  }

  // ============================================================================
  // DISCOUNT CODE MANAGEMENT
  // ============================================================================
  
  /**
   * Apply a single discount code via AJAX
   */
  async function applySingleDiscountCode(discountCode) {
    if (CONFIG.DEBUG) {
      console.log(`[Unified Promotions] 🎫 Attempting to apply discount code: ${discountCode}`);
    }
    
    try {
      const requestBody = { discount: discountCode };
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] Request body:', requestBody);
      }
      
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });

      if (CONFIG.DEBUG) {
        console.log(`[Unified Promotions] Response status: ${response.status} ${response.statusText}`);
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Unified Promotions] ❌ Discount application failed', { 
          code: discountCode,
          status: response.status, 
          error: errorText 
        });
        throw new Error(`Failed to apply discount: ${response.status}`);
      }

      const result = await response.json();
      if (CONFIG.DEBUG) {
        console.log(`[Unified Promotions] ✅ Discount code ${discountCode} applied successfully`, result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error applying discount code:', error);
      throw error;
    }
  }
  
  /**
   * Attempt to apply multiple discount codes
   */
  async function applyMultipleDiscountCodes(discountCodes) {
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🎯 Attempting to apply multiple discount codes:', discountCodes);
    }
    
    if (discountCodes.length === 0) {
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] No discount codes to apply');
      }
      return await removeAllDiscountCodes();
    }
    
    if (discountCodes.length === 1) {
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] Only one discount code, applying normally');
      }
      await applySingleDiscountCode(discountCodes[0]);
      currentDiscountCodes = [discountCodes[0]];
      return;
    }
    
    // Try comma-separated first (most likely to work)
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🧪 Trying comma-separated codes...');
    }
    try {
      const commaSeparated = discountCodes.join(',');
      await applySingleDiscountCode(commaSeparated);
      currentDiscountCodes = discountCodes;
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ Comma-separated codes worked!');
      }
      return;
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.warn('[Unified Promotions] ⚠️ Comma-separated approach failed:', error.message);
      }
    }
    
    // Try space-separated
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🧪 Trying space-separated codes...');
    }
    try {
      const spaceSeparated = discountCodes.join(' ');
      await applySingleDiscountCode(spaceSeparated);
      currentDiscountCodes = discountCodes;
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ Space-separated codes worked!');
      }
      return;
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.warn('[Unified Promotions] ⚠️ Space-separated approach failed:', error.message);
      }
    }
    
    // Fallback: Apply only the first (highest priority) discount code
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🔄 FALLBACK: Applying only first code:', discountCodes[0]);
    }
    await applySingleDiscountCode(discountCodes[0]);
    currentDiscountCodes = [discountCodes[0]];
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] ℹ️ Only single discount code applied.');
    }
  }
  
  /**
   * Remove all discount codes
   */
  async function removeAllDiscountCodes() {
    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🗑️ Removing all discount codes');
    }
    
    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ 
          discount: '' // Empty string removes discount
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Unified Promotions] ❌ Discount removal failed', { 
          status: response.status, 
          error: errorText 
        });
        throw new Error(`Failed to remove discount: ${response.status}`);
      }

      const result = await response.json();
      currentDiscountCodes = [];
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ All discount codes removed successfully', result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error removing discount codes:', error);
      throw error;
    }
  }

  // ============================================================================
  // GWP ITEM MANAGEMENT
  // ============================================================================
  
  /**
   * Add GWP item to cart
   */
  async function addGWPItem(currency, selectedGWPConfig = null) {
    const gwpConfig = selectedGWPConfig || getGWPCandidates(currency)[0];
    
    if (!gwpConfig) {
      log('No GWP config for currency', currency);
      return null;
    }

    const freshCart = await fetchFreshCart();
    if (freshCart) {
      const gwpItems = freshCart.items?.filter(isGWPLineItem) || [];
      const variantId = String(gwpConfig.variantId);

      if (gwpItems.length === 1 && String(gwpItems[0].id) === variantId) {
        log('GWP already in cart with correct variant, skipping add');
        return { items: gwpItems };
      }

      if (gwpItems.length > 0) {
        log('Existing GWP line(s) found before add, removing first');
        await removeGWPItem(freshCart);
      }
    }

    if (CONFIG.DEBUG) {
      console.log('[Unified Promotions] 🎁 Adding GWP item to cart', {
        currency,
        variantId: gwpConfig.variantId
      });
    }

    try {
      const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [{
            id: gwpConfig.variantId,
            quantity: 1,
            properties: {
              [CONFIG.GWP_PROPERTY_KEY]: CONFIG.GWP_PROPERTY_VALUE,
              ...(gwpConfig.extraProperties || {})
            }
          }]
        })
      });

      if (!response.ok) {
        console.error('[Unified Promotions] ❌ Failed to add GWP item', { status: response.status });
        throw new Error(`Failed to add GWP item: ${response.status}`);
      }

      const result = await response.json();
      const addedItem = result.items?.[0];
      if (addedItem?.url) {
        cacheGiftVariantUrl(gwpConfig.variantId, addedItem.url);
      }
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ GWP item added successfully', result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error adding GWP item:', error);
      throw error;
    }
  }

  /**
   * Remove GWP item from cart
   */
  async function removeGWPItem(cart) {
    if (!cart || !cart.items) return;

    const gwpItems = cart.items.filter(isGWPLineItem);

    if (gwpItems.length === 0) {
      log('No GWP item found to remove');
      return;
    }

    try {
      let result = null;

      for (const gwpItem of gwpItems) {
        if (CONFIG.DEBUG) {
          console.log('[Unified Promotions] 🗑️ Removing GWP item from cart', { 
            id: gwpItem.id, 
            key: gwpItem.key 
          });
        }

        const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({
            id: gwpItem.key,
            quantity: 0
          })
        });

        if (!response.ok) {
          console.error('[Unified Promotions] ❌ Failed to remove GWP item', { status: response.status });
          throw new Error(`Failed to remove GWP item: ${response.status}`);
        }

        result = await response.json();
      }

      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] ✅ GWP item removed successfully', result);
      }
      return result;

    } catch (error) {
      console.error('[Unified Promotions] ❌ Error removing GWP item:', error);
      throw error;
    }
  }

  // ============================================================================
  // GIFT COLOR SELECTION
  // ============================================================================

  function getCartLineImageUrl(lineItem) {
    if (!lineItem) return null;
    if (typeof lineItem.image === 'string' && lineItem.image) {
      return lineItem.image;
    }
    return lineItem.featured_image?.url || lineItem.featured_image?.src || null;
  }

  function setGiftLineImage(lineEl, imageUrl) {
    if (!lineEl || !imageUrl) return;

    const img = lineEl.querySelector('.line-item__image');
    if (!img) return;

    img.removeAttribute('srcset');
    img.removeAttribute('data-srcset');
    img.removeAttribute('data-src');
    img.src = imageUrl;
  }

  async function getGiftVariantImage(gift) {
    await fetchGiftVariantAvailability(gift);
    return giftImageCache.get(String(gift.variantId)) || null;
  }

  async function resolveGiftLineImage(gift, cart, addResult) {
    if (!gift) return null;

    const gwpItem = cart ? getCurrentGWPItem(cart) : null;
    const addedItem = addResult?.items?.[0];
    const giftVariantId = String(gift.variantId);

    const cartVariantId = gwpItem ? String(gwpItem.variant_id || gwpItem.id) : null;
    const addedVariantId = addedItem ? String(addedItem.variant_id || addedItem.id) : null;

    if (cartVariantId === giftVariantId) {
      const cartImage = getCartLineImageUrl(gwpItem);
      if (cartImage) return cartImage;
    }

    if (addedVariantId === giftVariantId) {
      const addedImage = getCartLineImageUrl(addedItem);
      if (addedImage) return addedImage;
    }

    return giftImageCache.get(giftVariantId) || (await getGiftVariantImage(gift));
  }

  function getGiftSwatchStyle(label) {
    const labelDown = (label || '').toLowerCase().trim();
    const entries = window.themeVariables?.settings?.colorSwatchStyleEntries || [];

    for (const entry of entries) {
      const nameDown = (entry.name || '').toLowerCase().trim();
      if (nameDown && labelDown.includes(nameDown)) {
        return entry.style;
      }
    }

    return `background-color: ${labelDown}`;
  }

  function applyGiftSwatchAppearance(itemEl, label) {
    if (!itemEl) return;
    itemEl.style.cssText = getGiftSwatchStyle(label);
  }

  function getCartDrawerSectionKey() {
    return 'mini-cart';
  }

  async function fetchCartForRefresh() {
    const sectionKey = getCartDrawerSectionKey();
    try {
      const response = await fetch(
        `${window.themeVariables.routes.cartUrl}.js?sections=${sectionKey}`
      );
      if (response.ok) {
        return response.json();
      }
    } catch (error) {
      log('Failed to fetch cart with drawer section', error);
    }

    const response = await fetch(`${window.themeVariables.routes.cartUrl}.js`);
    return response.json();
  }

  function bumpCartDrawerDelayForPendingGWP(cart) {
    const currency = getCurrentCurrency();
    const gwpConfig = getGWPConfig(currency);
    if (!gwpConfig || !cart) return;

    const subtotal = getCartSubtotalExcludingGWP(cart);
    if (subtotal < gwpConfig.thresholdCents || hasGWPItem(cart)) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && (cartDrawer.nextReplacementDelay || 0) < 900) {
      cartDrawer.nextReplacementDelay = 900;
    }
  }

  function isGiftSwatchDomComplete(swatchesEl) {
    if (!swatchesEl) return false;

    const expected = getGiftSwatchExpectedCount();
    const actual = swatchesEl.querySelectorAll('[data-gwp-color-swatch]').length;
    return expected > 0 && actual >= expected;
  }

  function updatePdpSwatchWrapClasses(wrap, label) {
    if (!wrap) return;

    const labelDown = (label || '').toLowerCase().trim();
    wrap.classList.add('pdp-color-swatch');
    wrap.classList.toggle('pdp-color-swatch--white', labelDown === 'white');
  }

  function updateGiftSwatchSelectionOnly(swatchesEl, selectedVariantId, candidates) {
    if (!swatchesEl) return;

    const selectorCandidates = candidates || getGWPGiftSelectorCandidates(getCurrentCurrency());
    const selectorRow = swatchesEl.closest('[data-gwp-selector-row]');

    swatchesEl.querySelectorAll('[data-gwp-color-swatch]').forEach((button) => {
      const variantId = button.getAttribute('data-variant-id');
      const wrap = button.closest('.pdp-color-swatch');
      const gift = selectorCandidates.find(
        candidate => String(candidate.variantId) === String(variantId)
      );
      const isSelected = selectedVariantId && String(variantId) === String(selectedVariantId);

      if (wrap) {
        updatePdpSwatchWrapClasses(wrap, gift?.label);
        wrap.classList.toggle('is-active', isSelected);
        if (gift?.label) {
          wrap.setAttribute('data-variant-title', gift.label);
        }
      }

      button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      if (isSelected) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });

    updateGiftSelectedLabel(selectorRow, selectedVariantId, selectorCandidates);
  }

  function updateGiftSwatchAvailabilityStates(swatchesEl, candidates) {
    if (!swatchesEl) return;

    const selectorCandidates = candidates || getGWPGiftSelectorCandidates(getCurrentCurrency());

    swatchesEl.querySelectorAll('[data-gwp-color-swatch]').forEach((button) => {
      const variantId = button.getAttribute('data-variant-id');
      const wrap = button.closest('.pdp-color-swatch');
      const gift = selectorCandidates.find(
        candidate => String(candidate.variantId) === String(variantId)
      );
      const isOutOfStock = gift && !isGiftVariantAvailable(gift);

      if (wrap) {
        wrap.classList.toggle('is-disabled', isOutOfStock);
        wrap.removeAttribute('hidden');
      }
      button.disabled = isOutOfStock;
    });
  }

  function getPdpSwatchWrapClasses(label) {
    const labelDown = (label || '').toLowerCase().trim();
    let classes = 'pdp-color-swatch';
    if (labelDown === 'white') {
      classes += ' pdp-color-swatch--white';
    }
    return classes;
  }

  function createGiftSwatchButton(gift) {
    const wrap = document.createElement('div');
    wrap.className = getPdpSwatchWrapClasses(gift.label);
    wrap.setAttribute('data-variant-title', gift.label || '');

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pdp-color-swatch__button';
    button.setAttribute('data-gwp-color-swatch', '');
    button.setAttribute('data-variant-id', String(gift.variantId));
    button.setAttribute('role', 'radio');
    button.setAttribute('aria-checked', 'false');
    button.setAttribute('aria-label', gift.label || String(gift.variantId));
    applyGiftSwatchAppearance(button, gift.label);

    const hiddenLabel = document.createElement('span');
    hiddenLabel.className = 'visually-hidden';
    hiddenLabel.textContent = gift.label || String(gift.variantId);
    button.appendChild(hiddenLabel);

    wrap.appendChild(button);
    return wrap;
  }

  function updateGiftSelectedLabel(selectorRow, selectedVariantId, candidates) {
    if (!selectorRow) return;

    const valueEl = selectorRow.querySelector('[data-gwp-selected-value]');
    if (!valueEl) return;

    const selectedGift = (candidates || []).find(
      gift => String(gift.variantId) === String(selectedVariantId)
    );
    valueEl.textContent = selectedGift?.label || '';
  }

  function syncGiftSwatchNodes(swatchesEl, selectorCandidates) {
    selectorCandidates.forEach((gift, index) => {
      const variantId = String(gift.variantId);
      let button = swatchesEl.querySelector(`[data-gwp-color-swatch][data-variant-id="${variantId}"]`);
      if (!button) {
        swatchesEl.appendChild(createGiftSwatchButton(gift));
        return;
      }

      const wrap = button.closest('.pdp-color-swatch');
      if (!wrap) return;

      wrap.removeAttribute('hidden');
      const currentAtIndex = swatchesEl.children[index];
      if (currentAtIndex !== wrap) {
        swatchesEl.insertBefore(wrap, swatchesEl.children[index] || null);
      }
    });
  }

  function buildGiftColorSwatches(swatchesEl, selectedVariantId, candidates, options = {}) {
    if (!swatchesEl) return;

    const cart = options.cart || null;
    const selectorCandidates = candidates || getGWPGiftSelectorCandidates(getCurrentCurrency());
    if (!selectorCandidates.length) return;

    const selectorRow = swatchesEl.closest('[data-gwp-selector-row]');
    const domIsComplete = !options.forceRebuild && isGiftSwatchDomComplete(swatchesEl);

    if (domIsComplete) {
      updateGiftSwatchSelectionOnly(swatchesEl, selectedVariantId, selectorCandidates);
      updateGiftSwatchAvailabilityStates(swatchesEl, selectorCandidates);
      return;
    }

    swatchesEl.removeAttribute('data-gwp-ready');
    syncGiftSwatchNodes(swatchesEl, selectorCandidates);

    swatchesEl.querySelectorAll('[data-gwp-color-swatch]').forEach((button) => {
      const variantId = button.getAttribute('data-variant-id');
      const wrap = button.closest('.pdp-color-swatch');
      const gift = selectorCandidates.find(
        candidate => String(candidate.variantId) === String(variantId)
      );
      const isSelected = selectedVariantId && String(variantId) === String(selectedVariantId);
      const isOutOfStock = gift && !isGiftVariantAvailable(gift);

      if (wrap) {
        updatePdpSwatchWrapClasses(wrap, gift?.label);
        wrap.classList.toggle('is-active', isSelected);
        wrap.classList.toggle('is-disabled', isOutOfStock);
        wrap.removeAttribute('hidden');
        if (gift?.label) {
          wrap.setAttribute('data-variant-title', gift.label);
        }
      }
      button.setAttribute('aria-checked', isSelected ? 'true' : 'false');
      if (isSelected) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
      button.disabled = isOutOfStock;

      if (gift?.label) {
        button.setAttribute('aria-label', gift.label);
        applyGiftSwatchAppearance(button, gift.label);
        const hiddenLabel = button.querySelector('.visually-hidden');
        if (hiddenLabel) {
          hiddenLabel.textContent = gift.label;
        }
      }
    });

    updateGiftSelectedLabel(selectorRow, selectedVariantId, selectorCandidates);
  }

  /**
   * Populate every gift color swatch row from CONFIG selector gifts.
   */
  async function renderGiftColorSwatches(cart, options = {}) {
    const swatchRows = document.querySelectorAll('[data-gwp-color-swatches]');
    if (!swatchRows.length) return 0;

    const currency = getCurrentCurrency();
    const candidates = getGWPGiftSelectorCandidates(currency);
    if (!candidates.length) return 0;

    const selectedVariantId = resolveGiftSwatchSelectedVariantId(cart, currency, swatchRows[0]);
    const allDomComplete = !options.forceRebuild &&
      Array.from(swatchRows).every((swatchesEl) => isGiftSwatchDomComplete(swatchesEl));

    swatchRows.forEach((swatchesEl) => {
      buildGiftColorSwatches(swatchesEl, selectedVariantId, candidates, { ...options, cart });
      markGiftSwatchContainerReady(swatchesEl, candidates);
    });

    if (!allDomComplete) {
      await Promise.all(candidates.map((gift) => fetchGiftVariantAvailability(gift)));
      swatchRows.forEach((swatchesEl) => {
        updateGiftSwatchAvailabilityStates(swatchesEl, candidates);
      });
    } else {
      Promise.all(candidates.map((gift) => fetchGiftVariantAvailability(gift))).then(() => {
        swatchRows.forEach((swatchesEl) => {
          updateGiftSwatchAvailabilityStates(swatchesEl, candidates);
        });
      });
    }

    const activeGift = candidates.find(
      gift => String(gift.variantId) === String(selectedVariantId || swatchRows[0]?.querySelector('[data-gwp-color-swatch]')?.getAttribute('data-variant-id'))
    );
    if (activeGift && swatchRows[0]) {
      const lineEl = swatchRows[0].closest('athena-line-item');
      resolveGiftLineImage(activeGift, cart).then((imageUrl) => {
        setGiftLineImage(lineEl, imageUrl);
      });
    }

    return candidates.length;
  }

  function getGiftSwatchExpectedCount() {
    return getGWPGiftSelectorCandidates(getCurrentCurrency()).length;
  }

  function getLineVariantIdFromSwatchesEl(swatchesEl) {
    return swatchesEl?.getAttribute('data-gwp-line-variant-id') || null;
  }

  function resolveGiftSwatchSelectedVariantId(cart, currency, swatchesEl) {
    return getSelectorDefaultVariantId(cart, currency) ||
      getLineVariantIdFromSwatchesEl(swatchesEl);
  }

  function markGiftSwatchContainerReady(swatchesEl, candidates) {
    if (!swatchesEl || !candidates?.length) return;
    if (!isGiftSwatchDomComplete(swatchesEl)) return;

    swatchesEl.setAttribute('data-gwp-expected-count', String(candidates.length));
    swatchesEl.setAttribute('data-gwp-ready', 'true');
  }

  function syncGiftColorSwatchesFromConfig(cart, options = {}) {
    const swatchRows = document.querySelectorAll('[data-gwp-color-swatches]');
    if (!swatchRows.length) return false;

    const currency = getCurrentCurrency();
    const candidates = getGWPGiftSelectorCandidates(currency);
    if (!candidates.length) return false;

    const selectedVariantId = resolveGiftSwatchSelectedVariantId(cart, currency, swatchRows[0]);

    swatchRows.forEach((swatchesEl) => {
      const forceRebuild = options.forceRebuild || !isGiftSwatchDomComplete(swatchesEl);
      if (forceRebuild) {
        swatchesEl.removeAttribute('data-gwp-ready');
      }

      buildGiftColorSwatches(swatchesEl, selectedVariantId, candidates, {
        cart,
        forceRebuild
      });
      markGiftSwatchContainerReady(swatchesEl, candidates);
    });

    return true;
  }

  function runGiftSwatchRenderPoll(cart, generation) {
    const maxWaitMs = 3000;
    const pollMs = 50;
    const start = Date.now();
    const expectedCount = getGiftSwatchExpectedCount();

    function tick() {
      if (generation !== giftSwatchRenderGeneration) return;

      const swatchesEl = document.querySelector('[data-gwp-color-swatches]');
      const elapsed = Date.now() - start;

      if (swatchesEl) {
        const forceRebuild = !isGiftSwatchDomComplete(swatchesEl);
        renderGiftColorSwatches(cart, { forceRebuild }).then(() => {
          if (generation !== giftSwatchRenderGeneration) return;

          const currentSwatchesEl = document.querySelector('[data-gwp-color-swatches]');
          const renderedCount = currentSwatchesEl?.querySelectorAll('[data-gwp-color-swatch]').length || 0;
          if (expectedCount && renderedCount >= expectedCount) {
            return;
          }
          if (elapsed < maxWaitMs) {
            setTimeout(tick, pollMs);
          }
        });
        return;
      }

      if (elapsed < maxWaitMs) {
        setTimeout(tick, pollMs);
      }
    }

    tick();
  }

  /**
   * Populate swatch row(s) once the gift line exists in the DOM.
   * Waits for the cart drawer section swap, then syncs active/disabled state.
   */
  function scheduleGiftColorSelectRender(cart) {
    if (!hasGWPItem(cart)) return;

    giftSwatchRenderGeneration += 1;
    const generation = giftSwatchRenderGeneration;

    if (giftSwatchRenderTimer) {
      clearTimeout(giftSwatchRenderTimer);
    }

    const cartDrawer = document.querySelector('cart-drawer');
    const replacementDelay = cartDrawer?.nextReplacementDelay || 0;
    const delayMs = replacementDelay + 100;

    syncGiftColorSwatchesFromConfig(cart);

    giftSwatchRenderTimer = setTimeout(() => {
      giftSwatchRenderTimer = null;
      if (generation !== giftSwatchRenderGeneration) return;
      runGiftSwatchRenderPoll(cart, generation);
    }, delayMs);
  }

  function getGWPLineElement() {
    return document.querySelector('[data-gwp-color-swatches]')?.closest('athena-line-item') || null;
  }

  function resolveGiftLineUrl(cart, gift, addResult) {
    const gwpItem = cart ? getCurrentGWPItem(cart) : null;
    const addedItem = addResult?.items?.find(
      (item) => String(item.variant_id || item.id) === String(gift?.variantId)
    ) || addResult?.items?.[0];
    const url = gwpItem?.url || addedItem?.url || (gift ? getGiftVariantUrl(gift) : null);

    if (gift && url) {
      cacheGiftVariantUrl(gift.variantId, url);
    }

    return url || null;
  }

  function syncGWPLineLinksFromCart(cart, gift, addResult) {
    const lineEl = getGWPLineElement();
    if (!lineEl) return;

    const gwpItem = cart ? getCurrentGWPItem(cart) : null;
    const url = resolveGiftLineUrl(cart, gift, addResult);

    if (url) {
      lineEl.querySelectorAll('.athena-line-item__title, .athena-line-item__image-wrapper').forEach((anchor) => {
        anchor.setAttribute('href', url);
      });
    }

    const titleEl = lineEl.querySelector('.athena-line-item__title');
    const title = gwpItem?.product_title || (gift && getGiftVariantTitle(gift));
    if (titleEl && title) {
      titleEl.textContent = title.replace(/\.$/, '');
    }
  }

  function handleGiftDrawerRendered(event) {
    const cart = event.detail && event.detail.cart;
    if (!cart || !hasGWPItem(cart)) return;

    syncGiftColorSwatchesFromConfig(cart);

    const currency = getCurrentCurrency();
    const gwpItem = getCurrentGWPItem(cart);
    const gift = getGWPCandidates(currency).find(
      (candidate) => String(candidate.variantId) === String(gwpItem?.variant_id || gwpItem?.id)
    );
    syncGWPLineLinksFromCart(cart, gift, null);
  }

  async function updateGWPLineFromCart(triggerEl, cart, gift, addResult) {
    if (!cart) return;

    const lineEl = triggerEl?.closest('athena-line-item') || getGWPLineElement();
    if (!lineEl) return;

    syncGWPLineLinksFromCart(cart, gift, addResult);

    const imageUrl = await resolveGiftLineImage(gift, cart, addResult);
    setGiftLineImage(lineEl, imageUrl);
  }

  function applyOptimisticGiftLineUpdate(lineEl, gift, variantId) {
    if (!lineEl || !gift) return;

    const variantKey = String(gift.variantId);
    const url = getGiftVariantUrl(gift);
    const title = getGiftVariantTitle(gift);
    const imageUrl = giftImageCache.get(variantKey);

    if (url) {
      lineEl.querySelectorAll('.athena-line-item__title, .athena-line-item__image-wrapper').forEach((anchor) => {
        anchor.setAttribute('href', url);
      });
    }

    if (title) {
      const titleEl = lineEl.querySelector('.athena-line-item__title');
      if (titleEl) {
        titleEl.textContent = title;
      }
    }

    if (imageUrl) {
      setGiftLineImage(lineEl, imageUrl);
    }

    const swatchesEl = lineEl.querySelector('[data-gwp-color-swatches]');
    if (swatchesEl && variantId) {
      swatchesEl.setAttribute('data-gwp-line-variant-id', String(variantId));
    }
  }

  async function refreshOptimisticGiftLineFromVariant(gift, lineEl, variantId) {
    if (!lineEl || !gift) return;

    await fetchGiftVariantAvailability(gift);
    applyOptimisticGiftLineUpdate(lineEl, gift, variantId);
  }

  function scheduleGiftCartSwap() {
    if (giftSwapDebounceTimer) {
      clearTimeout(giftSwapDebounceTimer);
    }

    giftSwapDebounceTimer = setTimeout(() => {
      giftSwapDebounceTimer = null;
      executeGiftCartSwap(giftSwapGeneration);
    }, GIFT_SWAP_DEBOUNCE_MS);
  }

  /**
   * Apply the latest gift selection to the cart. Coalesces rapid swatch clicks
   * into a single swap + refresh; UI updates happen immediately on click.
   */
  async function executeGiftCartSwap(triggerGeneration) {
    if (triggerGeneration !== giftSwapGeneration) return;

    if (isProcessing) {
      scheduleGiftCartSwap();
      return;
    }

    isProcessing = true;

    try {
      const currency = getCurrentCurrency();
      let didMutate = false;

      while (userSelectedGiftVariantId) {
        if (triggerGeneration !== giftSwapGeneration) return;

        const variantId = userSelectedGiftVariantId;
        const gift = getGWPGiftSelectorCandidates(currency).find(
          g => String(g.variantId) === String(variantId)
        );
        if (!gift) break;

        await fetchGiftVariantAvailability(gift);
        if (!isGiftVariantAvailable(gift)) break;

        let cart = await fetchFreshCart();
        if (!cart) break;

        const currentGWP = getCurrentGWPItem(cart);
        if (currentGWP && String(currentGWP.id) === String(variantId)) {
          syncGWPLineLinksFromCart(cart, gift, null);
          applyOptimisticGiftLineUpdate(getGWPLineElement(), gift, variantId);
          break;
        }

        await removeGWPItem(cart);
        await addGWPItem(currency, gift);
        didMutate = true;

        if (String(userSelectedGiftVariantId) === String(variantId)) {
          break;
        }
      }

      if (triggerGeneration !== giftSwapGeneration || !userSelectedGiftVariantId) {
        return;
      }

      const finalVariantId = userSelectedGiftVariantId;
      const finalGift = getGWPGiftSelectorCandidates(currency).find(
        g => String(g.variantId) === String(finalVariantId)
      );
      if (!finalGift) return;

      let cart = await fetchFreshCart();
      if (!cart) return;

      const subtotal = getCartSubtotalExcludingGWP(cart);
      const applicableDiscountCodes = getApplicableDiscountCodes(
        subtotal,
        currency,
        finalGift,
        cart
      );
      const gwpDiscountCode = getGWPDiscountCode(currency, finalGift);
      const currentGWP = getCurrentGWPItem(cart);
      const paidDuplicate = currentGWP && applicableDiscountCodes.includes(gwpDiscountCode)
        ? getPaidLineWithVariant(cart, currentGWP.id)
        : null;

      if (paidDuplicate && gwpDiscountCode) {
        isReanchoringGWPDiscount = true;
        try {
          cart = await applyGWPDiscountWithPaidDuplicate(
            cart,
            currency,
            finalGift,
            paidDuplicate
          );
          currentDiscountCodes = [...applicableDiscountCodes];
        } finally {
          isReanchoringGWPDiscount = false;
        }
      } else {
        await applyMultipleDiscountCodes(applicableDiscountCodes);
      }

      cart = await ensureGWPLineDiscounted(cart, currency, finalGift);
      cart = (await fetchFreshCart()) || cart;

      lastCartTotal = getCartSubtotalExcludingGWP(cart);
      lastCartSignature = getCartSignature(cart);
      lastCurrency = currency;

      syncGWPLineLinksFromCart(cart, finalGift, null);
      applyOptimisticGiftLineUpdate(getGWPLineElement(), finalGift, finalVariantId);

      if (didMutate) {
        const cartContent = await fetchCartForRefresh();
        if (triggerGeneration !== giftSwapGeneration) return;

        syncGWPLineLinksFromCart(cartContent, finalGift, null);
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            source: 'unified-promotions-manager',
            replacementDelay: 0
          }
        }));
      }
    } catch (e) {
      console.error('[Unified Promotions] ❌ Error applying gift color selection:', e);
    } finally {
      isProcessing = false;
      if (triggerGeneration !== giftSwapGeneration) {
        scheduleGiftCartSwap();
      }
    }
  }

  function applyGiftColorSelection(variantId, triggerEl) {
    if (!variantId) return;

    giftSwapGeneration += 1;

    const currency = getCurrentCurrency();
    const gift = getGWPGiftSelectorCandidates(currency).find(
      g => String(g.variantId) === String(variantId)
    );
    if (!gift) return;

    userSelectedGiftVariantId = variantId;
    lastCartSignature = null;
    lastCartTotal = null;

    const swatchesEl = triggerEl?.closest('[data-gwp-color-swatches]');
    const lineEl = triggerEl?.closest('athena-line-item');

    buildGiftColorSwatches(swatchesEl, variantId);
    applyOptimisticGiftLineUpdate(lineEl, gift, variantId);
    refreshOptimisticGiftLineFromVariant(gift, lineEl, variantId);

    scheduleGiftCartSwap();
  }

  /**
   * Click delegation for the cart-side gift color swatches rendered by
   * snippets/cart-content.liquid ([data-gwp-color-swatch]).
   */
  function handleGiftColorChange(event) {
    const swatch = event.target.closest('[data-gwp-color-swatch]');
    if (!swatch || swatch.disabled || swatch.closest('.pdp-color-swatch')?.classList.contains('is-active')) return;

    const variantId = swatch.getAttribute('data-variant-id');
    if (!variantId) return;

    applyGiftColorSelection(variantId, swatch);
  }

  /**
   * Repopulate the color swatches after the cart section re-renders.
   */
  function handleGiftSelectRender(event) {
    const cart = event.detail && event.detail.cart;
    if (!cart || !hasGWPItem(cart)) return;

    scheduleGiftColorSelectRender(cart);
  }

  // ============================================================================
  // CART PROCESSING
  // ============================================================================

  /**
   * Process cart changes and manage both GWP and discounts
   */
  async function processCartChange(cart) {
    if (CONFIG.DEBUG) {
      console.log('\n═══════════════════════════════════════════════════════');
      console.log('[Unified Promotions] 🔄 PROCESSING CART CHANGE');
      console.log('═══════════════════════════════════════════════════════');
    }
    
    const currency = getCurrentCurrency();

    if (!isConfiguredForCurrency(currency)) {
      log(`Not configured for currency ${currency}, skipping promotion logic`);
      return;
    }

    if (!cart) {
      log('No cart data provided');
      return;
    }

    if (isProcessing) {
      log('Currently processing, skipping to prevent loops');
      return;
    }

    isProcessing = true;

    try {
      if (countGWPItems(cart) > 1) {
        await dedupeGWPItems(cart);
        cart = (await fetchFreshCart()) || cart;
      }

      const subtotal = getCartSubtotalExcludingGWP(cart);
      const cartSignature = getCartSignature(cart);
      const cartContentsChanged = lastCartSignature !== cartSignature;

      if (cartContentsChanged && userSelectedGiftVariantId) {
        const chosenGift = getGWPGiftSelectorCandidates(currency).find(
          (gift) => String(gift.variantId) === String(userSelectedGiftVariantId)
        );
        if (chosenGift && cartContainsGiftProductOrVariant(cart, chosenGift)) {
          userSelectedGiftVariantId = null;
        }
      }

      let hasGWP = hasGWPItem(cart);
      let currentGWPItem = getCurrentGWPItem(cart);
      const gwpConfig = getGWPConfig(currency);

      await refreshSelectorGiftAvailability(currency);

      const selectedGWPConfig = getSelectedGWPConfig(cart, currency, subtotal);

      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] 📊 Cart Status:', {
          currency,
          subtotal: `${(subtotal / 100).toFixed(2)} ${currency}`,
          subtotalCents: subtotal,
          cartSignature,
          hasGWP: hasGWP,
          lastCartTotal: lastCartTotal,
          lastCartSignature: lastCartSignature,
          lastCurrency: lastCurrency,
          currentDiscountCodes: currentDiscountCodes,
          itemCount: cart.items.length,
          selectedGWP: selectedGWPConfig
        });
      }

      const totalChanged = lastCartTotal !== subtotal;
      const currencyChanged = lastCurrency !== currency;
      
      if (!totalChanged && !cartContentsChanged && !currencyChanged) {
        log('Cart total, contents, and currency unchanged, skipping processing');
        if (CONFIG.DEBUG) {
          console.log('═══════════════════════════════════════════════════════\n');
        }
        return;
      }

      if (currencyChanged) {
        if (CONFIG.DEBUG) {
          console.log('[Unified Promotions] 💱 Currency changed:', { from: lastCurrency, to: currency });
        }
        currentDiscountCodes = [];
      }

      lastCartTotal = subtotal;
      lastCartSignature = cartSignature;
      lastCurrency = currency;

      let needsRefresh = false;

      if (gwpConfig) {
        if (CONFIG.DEBUG) {
          console.log('\n[Unified Promotions] 📦 STEP 1: GWP Item Management');
          console.log('─────────────────────────────────────────────────────');
        }

        cart = (await fetchFreshCart()) || cart;
        hasGWP = hasGWPItem(cart);
        currentGWPItem = getCurrentGWPItem(cart);
        
        const shouldHaveGWP = selectedGWPConfig !== null;
        const hasCorrectGWP = currentGWPItem &&
          selectedGWPConfig &&
          String(currentGWPItem.id) === String(selectedGWPConfig.variantId);
        
        if (CONFIG.DEBUG) {
          console.log('[Unified Promotions] GWP Threshold Check:', {
            threshold: `${(gwpConfig.thresholdCents / 100).toFixed(2)} ${currency}`,
            subtotal: `${(subtotal / 100).toFixed(2)} ${currency}`,
            shouldHaveGWP,
            currentlyHasGWP: hasGWP,
            hasCorrectGWP,
            selectedGWP: selectedGWPConfig
          });
        }
        
        if (shouldHaveGWP && !hasGWP) {
          log(`Cart >= threshold - adding GWP item`);
          await addGWPItem(currency, selectedGWPConfig);
          needsRefresh = true;
          currentDiscountCodes = [];
        } else if (shouldHaveGWP && hasGWP && !hasCorrectGWP) {
          log(`Selected GWP changed - swapping GWP item`);
          await removeGWPItem(cart);
          await addGWPItem(currency, selectedGWPConfig);
          needsRefresh = true;
        } else if (!shouldHaveGWP && hasGWP) {
          log(`Cart < threshold - removing GWP item`);
          await removeGWPItem(cart);
          needsRefresh = true;
        } else if (countGWPItems(cart) > 1) {
          await dedupeGWPItems(cart, selectedGWPConfig?.variantId);
          needsRefresh = true;
        } else {
          log('GWP item status is correct, no action needed');
        }
      } else {
        log(`No GWP configured for ${currency}`);
      }

      // Step 2: Manage discount codes
      if (CONFIG.DEBUG) {
        console.log('\n[Unified Promotions] 💳 STEP 2: Discount Code Management');
        console.log('─────────────────────────────────────────────────────');
      }

      if (needsRefresh) {
        cart = (await fetchFreshCart()) || cart;
      }

      const applicableDiscountCodes = getApplicableDiscountCodes(
        subtotal,
        currency,
        selectedGWPConfig,
        cart
      );
      
      if (CONFIG.DEBUG) {
        console.log('[Unified Promotions] Discount Code Evaluation:', {
          currentCodes: currentDiscountCodes,
          applicableCodes: applicableDiscountCodes,
          needsUpdate: JSON.stringify(currentDiscountCodes) !== JSON.stringify(applicableDiscountCodes)
        });
      }

      // Compare arrays to see if they're different
      const codesChanged = JSON.stringify(currentDiscountCodes.sort()) !== JSON.stringify(applicableDiscountCodes.sort());
      
      if (codesChanged) {
        log('Discount codes need to be updated');

        const gwpDiscountCode = getGWPDiscountCode(currency, selectedGWPConfig);
        const gwpItem = getCurrentGWPItem(cart);
        const paidDuplicate = gwpItem && applicableDiscountCodes.includes(gwpDiscountCode)
          ? getPaidLineWithVariant(cart, gwpItem.id)
          : null;

        if (paidDuplicate && gwpDiscountCode) {
          isReanchoringGWPDiscount = true;
          try {
            cart = await applyGWPDiscountWithPaidDuplicate(
              cart,
              currency,
              selectedGWPConfig,
              paidDuplicate
            );
            currentDiscountCodes = [...applicableDiscountCodes];
          } finally {
            isReanchoringGWPDiscount = false;
          }
        } else {
          await applyMultipleDiscountCodes(applicableDiscountCodes);
        }

        needsRefresh = true;
      } else {
        log('Discount codes already correct, no action needed');
      }

      cart = (await fetchFreshCart()) || cart;
      const correctedCart = await ensureGWPLineDiscounted(cart, currency, selectedGWPConfig);
      if (correctedCart !== cart) {
        cart = correctedCart;
        needsRefresh = true;
      }

      // Step 3: Trigger cart refresh if anything changed
      if (CONFIG.DEBUG) {
        console.log('\n[Unified Promotions] 🔄 STEP 3: Cart Refresh');
        console.log('─────────────────────────────────────────────────────');
      }
      
      if (needsRefresh) {
        log('Fetching updated cart data...');
        const cartContent = await fetchCartForRefresh();

        log('Dispatching cart:refresh event');
        document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
          bubbles: true,
          detail: {
            cart: cartContent,
            source: 'unified-promotions-manager',
            replacementDelay: 0
          }
        }));
        scheduleGiftColorSelectRender(cartContent);
        log('Cart refresh completed');
      } else {
        log('No changes made, skipping cart refresh');
      }

      if (CONFIG.DEBUG) {
        console.log('\n[Unified Promotions] ✅ CART PROCESSING COMPLETE');
      }

    } catch (error) {
      console.error('[Unified Promotions] ❌ ERROR processing cart change:', error);
      console.error('[Unified Promotions] Error stack:', error.stack);
    } finally {
      isProcessing = false;
      if (CONFIG.DEBUG) {
        console.log('═══════════════════════════════════════════════════════\n');
      }
    }
  }

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================
  
  /**
   * Count of cart items that will render as <athena-line-item> elements.
   */
  function getExpectedRenderedLineCount(cart) {
    if (!cart || !Array.isArray(cart.items)) return 0;

    return cart.items.filter((item) => {
      const props = item.properties || {};
      if (props._hidden_from_cart) return false;

      const variantTitle = item.variant_title || (item.variant && item.variant.title) || '';
      if (variantTitle === 'Blades Ship Later') return false;

      return true;
    }).length;
  }

  /**
   * Defer processCartChange until the cart DOM matches the cart object.
   */
  function processCartChangeWhenRendered(cart) {
    processCartRenderGeneration += 1;
    const generation = processCartRenderGeneration;

    const expectedCount = getExpectedRenderedLineCount(cart);
    const maxWaitMs = 2000;
    const pollMs = 50;
    const start = Date.now();

    function tick() {
      if (generation !== processCartRenderGeneration) return;

      const renderedCount = document.querySelectorAll('athena-line-item').length;
      const elapsed = Date.now() - start;

      if (renderedCount === expectedCount || elapsed >= maxWaitMs) {
        processCartChange(cart);
      } else {
        setTimeout(tick, pollMs);
      }
    }

    tick();
  }

  /**
   * Handle cart:updated events
   */
  function handleCartUpdated(event) {
    log('Cart updated event received', {
      source: event.detail?.source,
      hasCart: !!event.detail?.cart
    });
    
    // Skip if this event came from our own operations
    if (event.detail && event.detail.source === 'unified-promotions-manager') {
      log('Ignoring self-triggered cart:updated');
      return;
    }

    if (event.detail && event.detail.cart) {
      bumpCartDrawerDelayForPendingGWP(event.detail.cart);
      processCartChangeWhenRendered(event.detail.cart);
    } else {
      log('Cart updated event has no cart data');
    }
  }

  /**
   * Handle cart:refresh events
   */
  function handleCartRefresh(event) {
    log('Cart refresh event received', {
      source: event.detail?.source,
      hasCart: !!event.detail?.cart
    });
    
    // Skip if this event came from our own operations
    if (event.detail && event.detail.source === 'unified-promotions-manager') {
      log('Ignoring self-triggered cart:refresh');
      return;
    }

    if (event.detail && event.detail.cart) {
      bumpCartDrawerDelayForPendingGWP(event.detail.cart);
      processCartChangeWhenRendered(event.detail.cart);
    } else {
      log('Cart refresh event has no cart data');
    }
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================
  
  /**
   * Initialize Unified Promotions Manager
   */
  function init() {
    if (CONFIG.DEBUG) {
      console.log('\n╔═══════════════════════════════════════════════════════╗');
      console.log('║   UNIFIED PROMOTIONS MANAGER - INITIALIZATION        ║');
      console.log('╚═══════════════════════════════════════════════════════╝');
      
      console.log('[Unified Promotions] 🔧 Configuration:', {
        debugMode: CONFIG.DEBUG,
        gwpCurrencies: Object.keys(CONFIG.GWP),
        tiersCurrencies: Object.keys(CONFIG.TIERS),
        gwpDetails: CONFIG.GWP,
        tiersDetails: CONFIG.TIERS
      });
    }
    
    const currency = getCurrentCurrency();
    log(`Current currency: ${currency}`);
    
    if (!isConfiguredForCurrency(currency)) {
      log(`Not configured for ${currency}, not initializing promotion logic`);
      return;
    }

    log(`${currency} is configured - initializing...`);

    refreshSelectorGiftAvailability(currency).catch(() => {});

    // Listen to cart events
    log('Attaching event listeners...');
    document.documentElement.addEventListener('cart:updated', handleCartUpdated);
    document.documentElement.addEventListener('cart:refresh', handleCartRefresh);
    // Delegated change handler for the cart-side gift color dropdown
    document.addEventListener('click', handleGiftColorChange);
    // Repopulate the dropdown options on every cart render (all sources)
    document.documentElement.addEventListener('cart:refresh', handleGiftSelectRender);
    document.documentElement.addEventListener('cart:updated', handleGiftSelectRender);
    document.documentElement.addEventListener('cart:drawer-rendered', handleGiftDrawerRendered);

    // Re-sync when the drawer opens (DOM may still have a single server-rendered option).
    const cartDrawer = document.getElementById('mini-cart');
    if (cartDrawer) {
      new MutationObserver(() => {
        if (!cartDrawer.open) return;
        fetch(`${window.themeVariables.routes.cartUrl}.js`)
          .then(response => response.json())
          .then(cart => scheduleGiftColorSelectRender(cart))
          .catch(() => {});
      }).observe(cartDrawer, { attributes: true, attributeFilter: ['open'] });
    }

    log('Event listeners attached (cart:updated, cart:refresh, gift color swatch/render)');

    // Initial check on page load
    if (window.themeVariables && window.themeVariables.routes && window.themeVariables.routes.cartUrl) {
      log('Fetching initial cart state...');
      fetch(`${window.themeVariables.routes.cartUrl}.js`)
        .then(response => response.json())
        .then(cart => {
          log('Initial cart loaded', {
            itemCount: cart.items?.length || 0,
            totalPrice: cart.total_price
          });
          processCartChange(cart);
          scheduleGiftColorSelectRender(cart);
        })
        .catch(error => {
          console.error('[Unified Promotions] ❌ Error fetching initial cart:', error);
        });
    } else {
      console.warn('[Unified Promotions] ⚠️ Cart URL not available in themeVariables');
    }

    log('Initialization complete\n');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // ============================================================================
  // GLOBAL API
  // ============================================================================

  // Expose configuration and status globally
  window.UnifiedPromotionsManager = {
    config: CONFIG,
    getCurrentCurrency: getCurrentCurrency,
    getCurrentDiscountCodes: () => currentDiscountCodes,
    getStatus: () => ({
      isProcessing,
      lastCartTotal,
      lastCartSignature,
      lastCurrency,
      currentDiscountCodes,
      currency: getCurrentCurrency()
    }),
    isConfiguredForCurrency: isConfiguredForCurrency,
    getGWPConfig: getGWPConfig,
    getGWPCandidates: getGWPCandidates,
    getSelectedGWPConfig: getSelectedGWPConfig,
    getTiersConfig: getTiersConfig
  };

  // Expose additional functions for debugging (only in debug mode)
  if (CONFIG.DEBUG) {
    log('Debug mode enabled - exposing additional functions');
    Object.assign(window.UnifiedPromotionsManager, {
      processCartChange,
      hasGWPItem,
      getCurrentGWPItem,
      getPaidCartItems,
      cartContainsGiftProductOrVariant,
      getCartSignature,
      getCartSubtotalExcludingGWP,
      getApplicableTier,
      getApplicableDiscountCodes,
      addGWPItem,
      removeGWPItem,
      applySingleDiscountCode,
      applyMultipleDiscountCodes,
      removeAllDiscountCodes
    });
  }

})();
  
  